import React, { useCallback, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Form, Row, Col, Card, Button } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { Editor } from '@tinymce/tinymce-react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

// import FormTooltip from '../../common/FormTooltip';
import VideoPlatformPreview from '../VideoPlatformPreview/VideoPlatformPreview';

PartnerSettingsForm.propTypes = {
    logo: PropTypes.object,
    setLogo: PropTypes.func,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    partner: PropTypes.object,
    onSubmit: PropTypes.func
};

const hardcodedMeta = {
    videoPlatforms: {
        youtube: 'youtube',
        vimeo: 'vimeo',
        other: 'other'
    },
    packageLevels: {
        brief: 'brief',
        extensive: 'extensive'
    }
};

const MAX_WORDCOUNT_WITH_BRIEF = 65;

function PartnerSettingsForm(props) {
    const { t } = useTranslation();
    const { partner, logo, setLogo, loading, saving, onLogoRemove, partnersMeta, isProspectumLiveEnabled, partnerPortalSettings } = props;
    const [tinyEditor, setTinyEditor] = useState(null);

    const meta = {
        ...hardcodedMeta,
        partnersMeta
    };

    /**
     * Dropzone configuration
     */
    const onDrop = useCallback(acceptedFiles => {
        const newFiles = acceptedFiles.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file),
            isNew: true
        }))
        setLogo(newFiles[0]);
    }, [setLogo]);
    const {getRootProps, getInputProps, isDragActive} = useDropzone({ accept: '.jpeg,.jpg,.png', onDrop });

    const _onLogoRemoval = () => onLogoRemove();

    /**
     * Formik configuration
     */
    const formik = useFormik({
        initialValues: {
            ...partner,
            event: {
                ...partner.event,
                ...(isProspectumLiveEnabled ? {
                    packageLevel: partner.event.packageLevel || 'extensive',
                    videoPlatform: partner.event.videoPlatform || 'youtube',
                    videoUrl: partner.event.videoUrl || '',
                    videoEmbedCode: partner.event.videoEmbedCode || '',
                } : {})
            }
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .required(t('form.name_required')),
            phone: Yup.string()
                .min(7, t('form.7_characters'))
                .nullable(),
        }),
        validate: (values) => {
            if (isProspectumLiveEnabled) {
                if (tinyEditor && values.event.packageLevel === 'brief') {
                    const wordCount = tinyEditor.plugins.wordcount.getCount();
                    if (wordCount > MAX_WORDCOUNT_WITH_BRIEF) {
                        return { description: t('partner.word_limit_error', { limit: MAX_WORDCOUNT_WITH_BRIEF }) }
                    }
                }

                if (values.event.packageLevel === 'extensive' && values.event.videoPlatform === 'other' && !values.event.videoEmbedCode.length) {
                    return { videoEmbedCode: t('partner.embed_code_required') };
                }
            }
        },
        onSubmit: values => {
            let submitValues = values;

            if (isProspectumLiveEnabled) {
                if (values.event.packageLevel === 'brief') {
                    submitValues = {
                        ...submitValues,
                        event: {
                            ...submitValues.event,
                            videoPlatform: null,
                            videoUrl: null
                        }
                    };
                }
            }

            props.onSubmit(submitValues);
        }
    });

    const { values, errors, touched } = formik;
    const { packageLevel, videoPlatform, videoUrl, videoEmbedCode } = values.event;

    /**
     * Handle event.description value change.
     *
     * @param {string} value
     */
    const _handleDescriptionChange = value => {
        formik.setValues({ ...values, event: {...values.event, description: value }});
    };

    return (
        <Form onSubmit={formik.handleSubmit}>
            <Form.Group as={Row} controlId="name" className="required partner-settings--partner-name">
                <Form.Label column sm={3}>{t('general.name')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.name || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                        isInvalid={errors.name && touched.name}
                    />
                    {errors.name && touched.name ? (
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    ) : null}
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="displayName" className="partner-settings--partner-display-name">
                <Form.Label column sm={3}>{t('general.display_name')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.displayName || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="homepage" className="partner-settings--partner-homepage">
                <Form.Label column sm={3}>{t('general.homepage')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.homepage || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="facebookPage" className="partner-settings--partner-facebook-page">
                <Form.Label column sm={3}>{t('general.facebook_page')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                            value={values.facebookPage || ''}
                            onChange={formik.handleChange}
                            disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="streetAddress" className="partner-settings--partner-street-address">
                <Form.Label column sm={3}>{t('general.street_address')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.streetAddress || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="postalCode" className="partner-settings--partner-postal-code">
                <Form.Label column sm={3}>{t('general.postal_code')}</Form.Label>
                <Col sm={3}>
                    <Form.Control
                        value={values.postalCode || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="city" className="partner-settings--partner-city">
                <Form.Label column sm={3}>{t('general.city')}</Form.Label>
                <Col sm={6}>
                    <Form.Control
                        value={values.city || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="phone" className="partner-settings--partner-phone-number">
                <Form.Label column sm={3}>{t('general.phone_number')}</Form.Label>
                <Col sm={4}>
                    <Form.Control
                        value={values.phone || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                        isInvalid={errors.phone && touched.phone}
                    />
                    {errors.phone && touched.phone ? (
                        <Form.Control.Feedback type="invalid">
                            {errors.phone}
                        </Form.Control.Feedback>
                    ) : null}
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="event.description" className="partner-settings--partner-description">
                <Form.Label column sm={3} className="partner-settings--description--label"><span>{t('general.description')}</span></Form.Label>
                <Col sm={9}>
                    <Editor
                        key={`partnerDescription-${loading}`}
                        id="description"
                        textareaName="description"
                        value={values.event.description}
                        onEditorChange={_handleDescriptionChange}
                        apiKey="f7xl8ix2ex377l8mbg0dgj4sy3nv3pyn27n7ulaghwsz2l52"
                        disabled={loading}
                        init={{
                            setup: (editor) => setTinyEditor(editor),
                            height: 400,
                            paste_word_valid_elements: "p,a,b,strong,i,em,h1,h2",
                            menubar: false,
                            plugins: [
                                'advlist autolink lists link image charmap print preview anchor',
                                'searchreplace visualblocks code fullscreen',
                                'insertdatetime media table paste code wordcount'
                            ],
                            toolbar: ' formatselect | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link unlink image | paste code | undo redo ',
                            block_formats: 'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Heading 4=h4;'
                        }}
                    />
                    {isProspectumLiveEnabled && packageLevel === 'brief' &&
                        <p className="text-muted">
                            {t('partner.word_limit', { limit: MAX_WORDCOUNT_WITH_BRIEF })}
                        </p>
                    }
                    {errors && errors.description ? (
                         <div className="text-danger">
                            {errors.description}
                        </div>
                    ) : null}
                </Col>
            </Form.Group>
            {/* show additional info field for lahti -use-case only */}
            {partnerPortalSettings && partnerPortalSettings.userCanEditOrders ?
                <Form.Group as={Row} controlId="event.additionalInfo" className="partner-settings--partner-additional-info">
                    <Form.Label column sm={3}>{t('general.additional_info')}</Form.Label>
                    <Col sm={9}>
                        <Form.Control
                            as="textarea"
                            rows={10}
                            value={values.event.additionalInfo || ''}
                            onChange={formik.handleChange}
                            disabled={saving}
                        />
                    </Col>
                </Form.Group>
            : null}
            <Form.Group as={Row} controlId="logo" className="partner-settings--partner-logo">
                <Form.Label column sm={3}>Logo</Form.Label>
                <Col sm={9}>
                    {logo &&
                        <div>
                            <img src={logo.preview} alt={partner.name || 'Logo'} style={{maxWidth: '200px', height: 'auto', width: '100%', marginBottom: '20px', marginRight: '20px'}} />
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={_onLogoRemoval}
                            >
                                {t('general.remove')}
                            </Button>
                        </div>
                    }
                    <div {...getRootProps()} className="DropzoneWrapper">
                        <input {...getInputProps()} />
                        {
                            isDragActive ?
                            <span>{t('general.drop_file_here')}</span> :
                            <span>{t('general.drag_and_drop_1')}<span className="text-primary" style={{ cursor: 'pointer' }}> {t('general.drag_and_drop_2')}</span></span>
                        }
                    </div>
                    <p className="text-muted">
                        {t('general.supported_filetypes_1')} <strong>PNG, JPG</strong>. {t('general.supported_filetypes_2')}
                    </p>
                </Col>
            </Form.Group>

            {/* Show ProspectumLIVE related settings if prospectumlive is enabled for event */}
            {isProspectumLiveEnabled && packageLevel === 'extensive' ?
                <fieldset className="partner-settings--plive-settings">
                    <legend className="text-muted">ProspectumLIVE</legend>
                    {/* <Form.Group as={Row} controlId="event.packageLevel">
                        <Form.Label column sm={3}>
                            {t('partner.package_level')}
                            <FormTooltip>
                                {t('partner.package_level_info')}
                            </FormTooltip>
                        </Form.Label>
                        <Col sm={3}>
                            <Form.Control
                                as="select"
                                name="event.packageLevel"
                                style={{ width: 200 }}
                                defaultValue={packageLevel || meta.packageLevels['brief']}
                                onChange={formik.handleChange}
                            >
                                {meta && Object.keys(meta.packageLevels).map(key => (
                                    <option
                                        key={key}
                                        value={meta.packageLevels[key]}
                                    >
                                        {t(`partner.${key}`)}
                                    </option>
                                ))}
                            </Form.Control>
                        </Col>
                    </Form.Group> */}
                    <Form.Group as={Row} controlId="even.videoPlatform">
                        <Form.Label column sm={3}>{t('partner.video_platform')}</Form.Label>
                        <Col sm={9}>
                            <Form.Control
                                as="select"
                                name="event.videoPlatform"
                                onChange={formik.handleChange}
                                style={{ textTransform: 'capitalize', width: 200 }}
                                disabled={packageLevel === 'brief'}
                                defaultValue={videoPlatform || 'youtube'}
                            >
                                {meta && Object.keys(meta.videoPlatforms).map(key => (
                                    <option
                                        key={key}
                                        value={meta.videoPlatforms[key]}
                                    >
                                        {key === 'other' ? t('partner.other') : key}
                                    </option>
                                ))}
                            </Form.Control>
                        </Col>
                    </Form.Group>
                    {/* Show video ID input only when videoPlatform is youtube or vimeo */}
                    {!['other'].includes(videoPlatform) &&
                        <>
                            <Form.Group as={Row} controlId="event.videoUrl">
                                <Form.Label column sm={3}>
                                    {t('partner.video_id')}
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        style={{ maxWidth: 200 }}
                                        type="text"
                                        name="event.videoUrl"
                                        autoComplete="off"
                                        onChange={formik.handleChange}
                                        disabled={packageLevel === 'brief'}
                                        defaultValue={videoUrl || ''}
                                    />
                                </Col>
                            </Form.Group>
                            <VideoPlatformPreview videoPlatform={videoPlatform} videoUrl={videoUrl} />
                        </>
                    }
                    {/* Show embed code text area only when videoplatform is other */}
                    {videoPlatform === 'other' &&
                        <Form.Group as={Row} controlId="event.videoEmbedCode">
                            <Form.Label column sm={3}>
                                {t('partner.embed_code')}
                            </Form.Label>
                            <Col sm={9}>
                                <Form.Control
                                    as="textarea"
                                    rows="3"
                                    name="event.videoEmbedCode"
                                    autoComplete="off"
                                    isInvalid={errors.videoEmbedCode && touched.videoEmbedCode}
                                    defaultValue={videoEmbedCode || ''}
                                    onChange={formik.handleChange}
                                    disabled={packageLevel === 'brief'}
                                />
                                {errors.videoEmbedCode && touched.videoEmbedCode ? (
                                    <div className="text-danger">
                                        {errors.videoEmbedCode}
                                    </div>
                                ) : null}
                            </Col>
                        </Form.Group>
                    }
                </fieldset>
            : null}

            <Card.Footer>
                <Form.Group as={Row}>
                    <Col sm={{span: 9, offset: 3}}>
                        <Button type="submit" variant="success" disabled={(loading || saving || errors.description)}>{t('general.save_changes')}</Button>
                    </Col>
                </Form.Group>
            </Card.Footer>
        </Form>
    )
}

export default PartnerSettingsForm;